import React, { useState, useEffect } from 'react';
import { ArrowRight, Clock, Users, X, MapPin, Mountain, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';


const Trekking = () => {
  const [treks, setTreks] = useState([]);
  const [selectedTrek, setSelectedTrek] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/treks`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setTreks(data);
        } else {
          console.error("Backend returned invalid format:", data);
        }
      })
      .catch(err => console.error("Error fetching treks:", err));
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (selectedTrek) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [selectedTrek]);

  return (
    <div className="container section">
      <h1 className="section-title">Trekking Adventures</h1>
      <p className="section-subtitle">
        Conquer the mighty Himalayas and explore breathtaking mountain trails across India.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
        gap: '2.5rem',
        marginTop: '3rem'
      }}>
        {treks.map((trek, index) => (
          <motion.div
            key={trek.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Image Section */}
            <div style={{ height: '260px', position: 'relative', overflow: 'hidden' }}>
              <img
                src={trek.image}
                alt={trek.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <span style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: trek.difficultyColor,
                color: 'white',
                padding: '6px 16px',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}>
                {trek.difficulty}
              </span>
            </div>

            {/* Content Section */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111', margin: '0 0 0.25rem 0' }}>
                {trek.name}
              </h3>
              <p style={{ color: '#6b7280', fontSize: '1rem', margin: '0 0 1rem 0' }}>
                {trek.location}
              </p>

              <p style={{ color: '#4b5563', fontSize: '1.05rem', lineHeight: '1.5', marginBottom: '2rem' }}>
                {trek.description}
              </p>

              {/* Stats Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '1rem',
                marginBottom: '2rem',
                paddingBottom: '1.5rem',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <div>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem', margin: 0 }}>Duration</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'bold', color: '#111' }}>
                    <Clock size={16} /> {trek.duration}
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem', margin: 0 }}>Altitude</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'bold', color: '#111' }}>
                    {trek.altitude}
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem', margin: 0 }}>Group Size</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'bold', color: '#111' }}>
                    <Users size={16} /> {trek.groupSize}
                  </div>
                </div>
              </div>

              {/* Spacer to push button to bottom */}
              <div style={{ flexGrow: 1 }}></div>

              <button
                onClick={() => setSelectedTrek(trek)}
                style={{
                  width: '100%',
                  padding: '0.85rem',
                  backgroundColor: '#ea580c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c2410c'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ea580c'}
              >
                View Details <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Modal / Frame Overlay */}
      <AnimatePresence>
        {selectedTrek && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem'
          }}>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedTrek(null)}
              style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)'
              }}
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '800px',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
                zIndex: 10000,
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <button
                onClick={() => setSelectedTrek(null)}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  zIndex: 10,
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.5)'}
              >
                <X size={20} />
              </button>

              <div style={{ height: '300px', width: '100%', flexShrink: 0, position: 'relative' }}>
                <img src={selectedTrek.image} alt={selectedTrek.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <span style={{
                  position: 'absolute',
                  bottom: '1rem',
                  right: '1rem',
                  backgroundColor: selectedTrek.difficultyColor,
                  color: 'white',
                  padding: '6px 16px',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                }}>
                  {selectedTrek.difficulty}
                </span>
              </div>

              <div style={{ padding: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <MapPin size={18} color="#ea580c" />
                  <span style={{ color: '#ea580c', fontWeight: 'bold', fontSize: '0.95rem' }}>{selectedTrek.location}</span>
                </div>

                <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111', margin: '0 0 1.5rem 0' }}>{selectedTrek.name}</h2>

                <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: '#4b5563', marginBottom: '2rem' }}>
                  {selectedTrek.longDescription}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1.5rem', backgroundColor: '#f9fafb', padding: '1.5rem', borderRadius: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Calendar size={18} color="#4b5563" />
                      <h4 style={{ margin: 0, fontWeight: 'bold', color: '#111' }}>Best Time</h4>
                    </div>
                    <p style={{ margin: 0, color: '#4b5563', paddingLeft: '1.8rem' }}>{selectedTrek.bestTimeToVisit}</p>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Clock size={18} color="#4b5563" />
                      <h4 style={{ margin: 0, fontWeight: 'bold', color: '#111' }}>Duration</h4>
                    </div>
                    <p style={{ margin: 0, color: '#4b5563', paddingLeft: '1.8rem' }}>{selectedTrek.duration}</p>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Mountain size={18} color="#4b5563" />
                      <h4 style={{ margin: 0, fontWeight: 'bold', color: '#111' }}>Max Altitude</h4>
                    </div>
                    <p style={{ margin: 0, color: '#4b5563', paddingLeft: '1.8rem' }}>{selectedTrek.altitude}</p>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Users size={18} color="#4b5563" />
                      <h4 style={{ margin: 0, fontWeight: 'bold', color: '#111' }}>Group Size</h4>
                    </div>
                    <p style={{ margin: 0, color: '#4b5563', paddingLeft: '1.8rem' }}>{selectedTrek.groupSize}</p>
                  </div>
                </div>

                <div style={{ marginTop: '2.5rem', textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      navigate(`/book-trek?trek=${encodeURIComponent(selectedTrek.name)}`);
                    }}
                    style={{
                      padding: '1rem 2rem',
                      backgroundColor: '#ea580c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '30px',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(0,95,21,0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,95,21,0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,95,21,0.3)';
                    }}
                  >
                    Book Trek
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Trekking;
